import type {
  AssetQuestionBinding,
  QuestionnaireExposureZone,
  TestedAssetType,
} from "./index";
import type {
  StudySpec,
  StudySpecResolvedExposure,
} from "./study-spec.generated";

/**
 * The one binding rule (plan Stage 3, S3.4 row 16; R3).
 *
 * How a question gathers its read of the tested stimulus follows the study's
 * resolved exposure, never the asset family, the recipe id or the method
 * string. Nest normalize is the one owner: Python assembly mints only what a
 * topic itself declares (the assets it shows and the read its scope states, a
 * tag), and `withStudyBinding` derives the mode, or releases the binding, from
 * the question's FINAL zone, so a question code moved is re-derived, never kept
 * as the zone PLAN drew.
 */

export type StudyBindingMode = "per_asset" | "all_assets";

/**
 * Whether the study gathers a separate read per asset outside the shared view:
 * the recipe repeats per-asset reads in lanes, or the assets are isolated.
 */
export function studyReadsPerAsset(
  exposure: StudySpecResolvedExposure,
): boolean {
  return exposure.perAssetReads === "lane_repeat" || exposure.mode === "isolated";
}

/**
 * The mode of a binding a question earns: `per_asset` outside the shared view
 * when the study reads per asset, `all_assets` otherwise. The shared view is
 * the one pass that shows every version together.
 */
export function studyBindingMode(
  zone: QuestionnaireExposureZone,
  exposure: StudySpecResolvedExposure,
): StudyBindingMode {
  return zone !== "shared_view" && studyReadsPerAsset(exposure)
    ? "per_asset"
    : "all_assets";
}

interface StudyBindingQuestion {
  assetBinding?: AssetQuestionBinding | null;
  response?: unknown;
}

function rowsAreTestedAssets(question: StudyBindingQuestion): boolean {
  const response = question.response as
    | { rows?: { source?: unknown } }
    | null
    | undefined;
  return (
    question.assetBinding?.rowSource === "tested_assets" ||
    response?.rows?.source === "tested_assets"
  );
}

/**
 * The binding normalize stamps on a question the author left unbound, or null
 * to keep it as authored (S3.4):
 *
 * - Only a lane-zone question earns one. Before the reveal nothing is shown,
 *   and the shared view runs as one pass by its declared zone (the compiled
 *   `exposureZone`), so it needs no stamp.
 * - It reads the stimulus per asset when the study reads per asset, and the
 *   one asset when there is one. When two or more versions are seen together
 *   it earns none: a read of every version at once belongs in the shared view.
 * - An authored binding, and a grid whose rows are the tested assets, are
 *   kept.
 *
 * Pure: no clock, no I/O, and no reading of family, tags, keys or wording.
 */
export function studyBindingFor(
  question: StudyBindingQuestion,
  zone: QuestionnaireExposureZone,
  spec: StudySpec | null,
): AssetQuestionBinding | null {
  if (!spec || spec.resolved.stimulus.count === 0 || zone !== "lane") {
    return null;
  }
  if (question.assetBinding || rowsAreTestedAssets(question)) return null;
  const { exposure, stimulus } = spec.resolved;
  if (!studyReadsPerAsset(exposure) && exposure.mode !== "single") return null;
  const assetTypes: Array<TestedAssetType> = [...stimulus.families];
  return { mode: studyBindingMode(zone, exposure), assetTypes };
}

/**
 * The read a derived binding states, minted by Python assembly from the
 * topic's declared `assetScope` (`cross_asset`, `monadic_body`) or, with none,
 * `study`: the read follows the study's exposure.
 */
export const BINDING_READS = ["cross_asset", "monadic_body", "study"] as const;
export type BindingRead = (typeof BINDING_READS)[number];

// shortcut: binding read kind carried as a tag because the pinned Argo analysis
// image validates questions with extra=forbid; revisit when the Argo image
// bump (release blocker) lands -> typed field on `assetBinding`.
const BINDING_READ_TAG_PREFIX = "binding:read:";

/** The tag a derived binding carries; the only writer here (Python's is `binding_read.py`). */
export function bindingReadTag(read: BindingRead): string {
  return `${BINDING_READ_TAG_PREFIX}${read}`;
}

/** The read a question's tags state, or null: its binding is authored. The one parser. */
export function bindingReadOf(
  tags: ReadonlyArray<unknown> | null | undefined,
): BindingRead | null {
  for (const tag of Array.isArray(tags) ? tags : []) {
    if (typeof tag !== "string" || !tag.startsWith(BINDING_READ_TAG_PREFIX)) continue;
    const read = BINDING_READS.find((value) => tag === bindingReadTag(value));
    if (read) return read;
  }
  return null;
}

/**
 * The mode a derived binding takes in a zone: a cross-asset read and the
 * shared view read every version together; a `monadic_body` read is its lane
 * read in every zone after the reveal (each version on its own under a shared
 * exposure, else as the study's lanes read). A per-version read is never
 * rebound to every version in the shared view: there it is the mixed-mode
 * order the owner rules out (09-30, 10-21), which the validator states
 * (`shared_view_question_per_version`) and the placer moves back before the
 * shared view. Anything else follows the study (`studyBindingMode`).
 */
export function derivedBindingMode(
  read: BindingRead,
  zone: QuestionnaireExposureZone,
  exposure: StudySpecResolvedExposure,
): StudyBindingMode {
  if (read === "cross_asset") return "all_assets";
  if (read === "monadic_body") return exposure.mode === "shared" ? "per_asset" : studyBindingMode("lane", exposure);
  return studyBindingMode(zone, exposure);
}

interface ZoneBindingQuestion extends StudyBindingQuestion {
  tags?: ReadonlyArray<string> | null;
  assetRefs?: ReadonlyArray<{ role?: unknown }> | null;
  postReveal?: unknown;
}

const withoutReadTags = (tags: ReadonlyArray<string> | null | undefined): Array<string> =>
  (tags ?? []).filter((tag) => bindingReadOf([tag]) === null);

/**
 * A derived binding (one that carries a read tag) in its final zone:
 *
 * - before the reveal nothing is shown, so the binding, the stimulus refs it
 *   implied, the stamped `postReveal` and the tag are released;
 * - elsewhere its mode is `derivedBindingMode` for that zone. Without a spec
 *   there is no exposure to derive from, and the mode stays as minted.
 *
 * A binding with no tag, one that shows nothing (`none`), and a grid whose rows
 * are the tested assets are authored and returned as they are.
 */
export function withZoneBinding<Q extends ZoneBindingQuestion>(
  question: Q,
  zone: QuestionnaireExposureZone,
  spec: StudySpec | null,
): Q {
  const read = bindingReadOf(question.tags);
  const binding = question.assetBinding;
  if (read === null || !binding || binding.mode === "none" || rowsAreTestedAssets(question)) {
    return question;
  }
  if (zone === "pre") {
    const { assetBinding: _binding, postReveal: _postReveal, ...rest } = question;
    const refs = (question.assetRefs ?? []).filter((ref) => ref?.role !== "stimulus");
    return {
      ...rest,
      tags: withoutReadTags(question.tags),
      assetRefs: refs.length > 0 ? refs : undefined,
    } as unknown as Q;
  }
  if (!spec) return question;
  const mode = derivedBindingMode(read, zone, spec.resolved.exposure);
  return mode === binding.mode ? question : { ...question, assetBinding: { ...binding, mode } };
}

/**
 * The question as normalize leaves it: `withZoneBinding`, and an unbound lane
 * question earns the study's binding (`studyBindingFor`) with the `study` read,
 * so a later move derives it again instead of keeping it as if authored.
 */
export function withStudyBinding<Q extends ZoneBindingQuestion>(
  question: Q,
  zone: QuestionnaireExposureZone,
  spec: StudySpec | null,
): Q {
  const derived = withZoneBinding(question, zone, spec);
  if (derived.assetBinding) return derived;
  const stamped = studyBindingFor(derived, zone, spec);
  return stamped
    ? { ...derived, assetBinding: stamped, tags: [...withoutReadTags(derived.tags), bindingReadTag("study")] }
    : derived;
}
